import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Menu, type MenuItem } from "./Menu";
import { Scrim, useEscape } from "./Sheet";
import { cn } from "./utils";

/** Where a menu opens: the pointer for a right-click, or the button that opened it. */
export type MenuAnchor = { x: number; y: number };

/** The title row of a phone menu: what it acts on, such as a person's name and photo. */
export type MenuHeading = { title: string; detail?: string; icon?: ReactNode };

const EDGE = 8;
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Puts focus back on what opened a menu: the element itself, or the first thing in it that takes focus. */
export function returnFocus(el: HTMLElement | null | undefined) {
  if (!el?.isConnected) return;
  const target = el.matches(FOCUSABLE) ? el : el.querySelector<HTMLElement>(FOCUSABLE);
  target?.focus({ preventScroll: true });
}

/** Closes the whole menu before running a chosen item, inside submenus too. */
function closingFirst(items: MenuItem[], close: () => void): MenuItem[] {
  return items.map((item) => {
    if (item.type === "separator") return item;
    if (item.type === "submenu") return { ...item, items: closingFirst(item.items, close) };
    return {
      ...item,
      onSelect: () => {
        close();
        item.onSelect();
      },
    };
  });
}

/**
 * Keeps an open menu or popover inside the window, 8 px from each edge, and moves it again when
 * the window changes size.
 */
export function useClampedPlace(anchor: MenuAnchor | null, active: boolean, contentKey?: unknown) {
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const measure = useCallback(() => {
    if (!anchor || !panel.current) return;
    const { width, height } = panel.current.getBoundingClientRect();
    setPlace({
      left: Math.max(EDGE, Math.min(anchor.x, window.innerWidth - width - EDGE)),
      top: Math.max(EDGE, Math.min(anchor.y, window.innerHeight - height - EDGE)),
    });
  }, [anchor]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured again when what it shows changes
  useLayoutEffect(() => {
    if (active) measure();
    else setPlace(null);
  }, [active, measure, contentKey]);
  useEffect(() => {
    if (!active) return;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active, measure]);
  return { panel, place };
}

/**
 * An item's actions. On desktop it opens where the pointer is, kept inside the window. On phones
 * it rises from the bottom of the screen as a sheet titled with what it acts on, as a long press
 * does in the phone's own apps. Focus goes back to what opened it when it closes.
 */
export function ContextMenu({
  label,
  items,
  anchor,
  sheet = false,
  onClose,
  heading,
  opener,
}: {
  label: string;
  items: MenuItem[];
  anchor: MenuAnchor | null;
  /** A phone: the menu comes up from the bottom. */
  sheet?: boolean;
  onClose: () => void;
  /** The sheet's title row. Without it the sheet is titled with the label. */
  heading?: MenuHeading;
  /** What opened the menu, which gets focus back when it closes. */
  opener?: HTMLElement | null;
}) {
  const open = anchor !== null;
  const { panel, place } = useClampedPlace(anchor, open && !sheet);
  useEscape(open, onClose);

  const openerRef = useRef(opener);
  openerRef.current = opener;
  useEffect(() => {
    if (!open) return;
    const from = openerRef.current ?? (document.activeElement as HTMLElement | null);
    return () => returnFocus(from);
  }, [open]);

  if (!anchor) return null;
  const choose = closingFirst(items, onClose);

  if (sheet) {
    const title = heading?.title ?? label;
    return (
      <div data-menu-layer="" className="fixed inset-0 z-50 flex items-end justify-center">
        <Scrim onClick={onClose} />
        <section
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="relative flex max-h-[88dvh] w-full flex-col rounded-t-sheet bg-linen px-2.5 pt-2.5 pb-[max(16px,env(safe-area-inset-bottom))] shadow-menu"
        >
          <span
            aria-hidden="true"
            className="mx-auto mb-2 h-[5px] w-10 flex-none rounded-[5px] bg-line-strong"
          />
          <div className="flex flex-none items-center gap-3 border-b border-line px-3 pt-1 pb-3">
            {heading?.icon}
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-base font-semibold text-ink">{title}</span>
              {heading?.detail && (
                <span className="truncate text-[13px] text-ink-secondary">{heading.detail}</span>
              )}
            </span>
          </div>
          <Menu
            label={label}
            items={choose}
            onClose={onClose}
            variant="sheet"
            className="min-h-0 flex-1 pt-1"
          />
        </section>
      </div>
    );
  }

  return (
    // A pointer shortcut only, like a scrim. Keyboard users close the menu with Escape or Tab.
    // biome-ignore lint/a11y/noStaticElementInteractions: a click outside the menu closes it
    <div
      data-menu-layer=""
      className="fixed inset-0 z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        // Hidden by opacity while it is measured, so its first item can still take focus.
        className={cn("fixed", !place && "pointer-events-none opacity-0")}
        style={place ?? { left: anchor.x, top: anchor.y }}
      >
        <Menu
          label={label}
          items={choose}
          onClose={onClose}
          style={{ maxHeight: `calc(100dvh - ${EDGE * 2}px)` }}
        />
      </div>
    </div>
  );
}
